import { BadgeCheckIcon, InfoIcon, UserIcon } from "lucide-react";
import React, { useEffect } from "react";
import { Organization } from "verdi-common/apiTypes/organization";
import { SessionHandler } from "verdi-web-client";
import { UserCreateUpdateBody, UserProfile } from "@/api/orval/generated/schemas";
import { FormMultiSelectGroup } from "@/components/multi-select-group/form-multi-select-group";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Fieldset, FieldsetLegend } from "@/components/ui/fieldset";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { AccountObjectsCombobox } from "./account-objects-dropdown";
import { PasswordComponent } from "./password-component";

export type NewUserProfile = Omit<UserProfile, "userId" | "createdTime" | "status">;

/** Account-level role designations stored on the Descope `title` custom attribute. */
const ACCOUNT_ROLE_OPTIONS: { value: string; label: string }[] = [
    { value: "admin", label: "Admin" },
    { value: "farm_manager", label: "Farm Manager" },
    { value: "agronomist", label: "Agronomist" },
    { value: "farm_worker", label: "Farm Worker" },
];

function VerifiedBadge({ verified, ...props }: { verified: boolean }) {
    return (
        <Badge
            title={verified ? "Verified" : "Unverified"}
            className={"rounded-full p-1"}
            variant={verified ? "success" : "warning"}
            {...props}
        >
            <BadgeCheckIcon className={"size-4"} />
        </Badge>
    );
}

/**
 * Filters and cleans an array of unknown values to return only non-empty strings.
 *
 * @param values - Array of unknown values to process
 * @returns Array of trimmed, non-empty strings
 *
 * @example
 * ```typescript
 * const mixed = ["  hello  ", "", "world", null, 123, "  ", "test"];
 * const result = trimAndFilterEmptyStrings(mixed);
 * // Returns: ["hello", "world", "test"]
 * ```
 */
function trimAndFilterEmptyStrings(values: unknown[]): string[] {
    return values
        .filter((id): id is string => typeof id === "string")
        .map((id) => id.trim())
        .filter((id) => id.length > 0);
}

/**
 * Validates a phone number against a strict E.164 international numbering format.
 *
 * E.164 format details:
 * - Structure: [+][country code][subscriber number including area code]
 * - Must start with '+' followed by a non-zero digit
 * - Contains only digits after the leading '+'
 * - Maximum length: 15 digits total (excluding the '+')
 *
 * References:
 * - Descope docs: https://docs.descope.com/flows/screens/inputs/phone-numbers#e164-standard
 * - Wikipedia: https://en.wikipedia.org/wiki/E.164
 *
 * @param phone - The value to validate (string or unknown).
 * @returns True if the value is a string matching E.164 constraints; otherwise false.
 */
function isPhoneNumberE164Format(phone: string | unknown): boolean {
    const BASE_E164_FORMAT = /^\+[1-9]\d{10,14}$/;
    if (typeof phone !== "string") {
        return false;
    }
    return BASE_E164_FORMAT.test(phone);
}

/**
 * Sanitizes a raw phone input into an E.164-like candidate prior to validation.
 *
 * Steps:
 * 1. Trim whitespace.
 * 2. Remove common formatting characters (parentheses, spaces, dashes, dots).
 * 3. Preserve a leading '+' if present.
 * 4. Strip all non-digit characters (except the preserved leading '+').
 *
 * Limitations:
 * - Does NOT guarantee validity; final format enforcement is performed by `isPhoneNumberE164Format`.
 * - Does not infer country codes; assumes provided digits are already correct.
 *
 * @param raw - User-entered phone string.
 * @returns A sanitized string containing an optional leading '+' followed by digits only.
 */
function sanitizePhoneInput(raw: string): string {
    let value = raw.trim();
    value = value.replace(/[()\s\-.]/g, "");
    if (value.startsWith("+")) {
        value = `+${value.slice(1).replace(/[^0-9]/g, "")}`;
    } else {
        value = `${value.replace(/[^0-9]/g, "")}`;
    }
    return value;
}

function getContactValues(formValues: Record<string, unknown>, emailOverride?: string, phoneOverride?: string) {
    const email = emailOverride ?? (typeof formValues.email === "string" ? formValues.email.trim() : "");
    const phone = phoneOverride ?? (typeof formValues.phone === "string" ? formValues.phone.trim() : "");
    return { email, phone };
}

function validateEmailField(value: unknown, formValues: Record<string, unknown>): string | null {
    const email = typeof value === "string" ? value.trim() : "";
    const { phone } = getContactValues(formValues, email);
    if (email.length === 0 && phone.length === 0) {
        return "Email or phone is required";
    }
    if (email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return "Enter a valid email address";
    }
    return null;
}

function validatePhoneField(value: unknown, formValues: Record<string, unknown>): string | null {
    const phone = typeof value === "string" ? value.trim() : "";
    const { email } = getContactValues(formValues, undefined, phone);
    if (email.length === 0 && phone.length === 0) {
        return "Email or phone is required";
    }
    if (phone.length > 0 && !isPhoneNumberE164Format(phone)) {
        return "Use E.164 format e.g. +15551231234";
    }
    return null;
}

export function validateOrganizationsField(value: unknown): string | null {
    const organizations = Array.isArray(value) ? value : [];
    const organizationValues = organizations.map((item) => {
        if (typeof item === "string") {
            return item;
        }
        if (typeof item === "object" && item !== null && "value" in item) {
            return String(item.value);
        }
        return "";
    });
    const validOrganizations = trimAndFilterEmptyStrings(organizationValues);
    if (validOrganizations.length === 0) {
        return "At least one organization is required";
    }
    return null;
}

export type UserDetailsPanelProps = {
    user?: NewUserProfile;
    onClose: () => void;
    onSave?: (updatedUser: UserProfile) => void;
};

export function UserDetailsPanel({ user, onClose, onSave }: UserDetailsPanelProps) {
    const [organizationsLoading, setOrganizationsLoading] = React.useState(false);
    const [organizations, setOrganizations] = React.useState<{ value: string; label: string }[]>([]);
    const [role, setRole] = React.useState<string>(user?.title ?? "");
    const [formAlert, setFormAlert] = React.useState<{
        variant: "info" | "error";
        title: string;
        description: React.ReactNode | string;
    } | null>(null);

    useEffect(() => {
        const fetch = async () => {
            setOrganizationsLoading(true);
            const response = await SessionHandler.doFetch("organizations/search", {
                headers: { "Content-Type": "application/json" },
                method: "POST",
                body: JSON.stringify({ limit: 500 }),
            });
            if (response.ok) {
                const data = await response.json();
                setOrganizations(
                    data.organizations.map((organization: Organization) => ({
                        value: `organization_id:${organization.id}`,
                        label: organization.name,
                    })),
                );
            }
            setOrganizationsLoading(false);
        };
        fetch();
    }, []);

    const newUser = !user;
    if (!user) {
        user = {
            loginIds: [],
            accountObjects: [],
            password: false,
            givenName: "",
            familyName: "",
            email: "",
            phone: "",
            verifiedEmail: false,
            verifiedPhone: false,
            title: "",
        };
    }

    if (!user.givenName && !user.familyName && user.name) {
        user.givenName = user.name.split(" ")[0];
        user.familyName = user.name.split(" ").slice(1).join(" ");
    }

    const displayName = `${user.givenName} ${user.familyName}`.trim() || user.loginIds[0] || "";

    function getChangedFormValues(
        existingUser: UserProfile | NewUserProfile,
        formValues: Record<string, FormDataEntryValue | FormDataEntryValue[] | null>,
    ): Record<string, string | string[] | boolean> {
        const changedFormValues: Record<string, string | string[] | boolean> = {};

        // Only update if changed
        const stringFields: (keyof NewUserProfile)[] = ["givenName", "familyName", "email", "phone"];
        stringFields.forEach((field) => {
            const value = formValues[field];
            if (typeof value === "string") {
                const trimmedValue = value.trim();
                if (trimmedValue !== existingUser[field]) {
                    changedFormValues[field] = trimmedValue;
                }
            }
        });

        const passwordEnabled = formValues.passwordEnabled === "on";
        if (passwordEnabled !== existingUser.password) {
            changedFormValues.passwordEnabled = passwordEnabled;
        }

        const passwordExpired = formValues.passwordExpired === "on";
        if (passwordExpired) {
            changedFormValues.passwordExpired = true;
        }

        if (typeof formValues.confirmPassword === "string" && formValues.confirmPassword.length > 0) {
            changedFormValues.confirmPassword = formValues.confirmPassword;
        }

        // Handle array fields - only update if array has non-empty values
        if (Array.isArray(formValues.loginIds)) {
            // Nuance: only ensure new values are distinct to ensure we remove duplicates
            // from existing values that would have set equality.
            const newLoginIds = new Set(trimAndFilterEmptyStrings(formValues.loginIds));
            const existingLoginIds = existingUser.loginIds;
            const hasChanges =
                existingLoginIds.length !== newLoginIds.size ||
                !Array.from(newLoginIds).every((id) => existingLoginIds.includes(id)) ||
                !existingLoginIds.every((id) => newLoginIds.has(id));

            if (newLoginIds.size > 0 && hasChanges) {
                changedFormValues.loginIds = Array.from(newLoginIds);
            }
        }

        if (Array.isArray(formValues.accountObjects)) {
            // Nuance: only ensure new values are distinct to ensure we remove duplicates
            // from existing values that would have set equality.
            const newAccountObjects = new Set(trimAndFilterEmptyStrings(formValues.accountObjects));
            const existingAccountObjects = existingUser.accountObjects;
            const hasChanges =
                existingAccountObjects.length !== newAccountObjects.size ||
                !Array.from(newAccountObjects).every((id) => existingAccountObjects.includes(id)) ||
                !existingAccountObjects.every((id) => newAccountObjects.has(id));

            if (hasChanges) {
                changedFormValues.accountObjects = Array.from(newAccountObjects);
            }
        }
        return changedFormValues;
    }

    const userSaveHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const formValues = {
            givenName: formData.get("givenName"),
            familyName: formData.get("familyName"),
            email: formData.get("email"),
            phone: formData.get("phone"),
            loginIds: formData.getAll("loginIds"),
            passwordEnabled: formData.get("passwordEnabled"),
            passwordExpired: formData.get("passwordExpired"),
            newPassword: formData.get("newPassword"),
            confirmPassword: formData.get("confirmPassword"),
            accountObjects: formData.getAll("organizations"),
        };

        const { passwordEnabled, passwordExpired, confirmPassword, accountObjects, ...userUpdates } =
            getChangedFormValues(user, formValues);
        const roleChanged = role !== (user.title ?? "");
        const customAttributes = {
            ...(accountObjects ? { accountObjects } : {}),
            ...(roleChanged ? { title: role } : {}),
        };
        const patchUserRequest: UserCreateUpdateBody = {
            // if the user is a new user, we don't need to provide a userId
            userId: (user as UserProfile)?.userId,
            // Use the first existing user loginId if it is not null/undefined; otherwise
            // fall back to the first new loginId from userUpdates (if provided), else empty string.
            loginId: user?.loginIds?.[0] ?? (Array.isArray(userUpdates.loginIds) ? userUpdates.loginIds[0] : ""),
            options: {
                ...(Object.keys(customAttributes).length > 0 ? { customAttributes } : {}),
                ...userUpdates,
            },
        };
        if (typeof confirmPassword === "string") {
            patchUserRequest.password = confirmPassword;
            patchUserRequest.passwordType = "active";
        }
        if (passwordEnabled === false) {
            patchUserRequest.password = "";
        }
        if (passwordExpired === true) {
            patchUserRequest.passwordType = "temporary";
        }

        const needsPatch =
            Object.keys(patchUserRequest.options).length > 0 ||
            Object.hasOwn(patchUserRequest, "password") ||
            Object.hasOwn(patchUserRequest, "passwordType");
        if (needsPatch) {
            // setFormAlert({ variant: "info", title: "Saving", description: ( <pre className={"font-mono"}>{`${JSON.stringify(user, null, 2)}\n\n${JSON.stringify(patchUserRequest, null, 2)}`}</pre> ) });
            try {
                const updateResponse = await SessionHandler.doFetch("users/update", {
                    headers: { "Content-Type": "application/json" },
                    method: "POST",
                    body: JSON.stringify(patchUserRequest),
                });
                if (!updateResponse.ok) {
                    throw new Error("Failed to update user", {
                        cause: await updateResponse.text(),
                    });
                }
                if (onSave) {
                    onSave(await updateResponse.json());
                }
                onClose();
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? ((error.cause as string) ?? error.message) : "Unknown error occurred";
                setFormAlert({
                    variant: "error",
                    title: "Save failed",
                    description: errorMessage,
                });
            }
        } else {
            setFormAlert(null);
            onClose();
        }
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle
                    className={
                        "flex flex-row gap-2 pb-2 mb-0 items-end border-b border-secondary font-bold text-lg sm:text-2xl"
                    }
                >
                    <UserIcon className={"size-8 sm:size-10 stroke-3 stroke-primary"} />
                    {newUser ? (
                        "Add User"
                    ) : (
                        <Breadcrumb>
                            <BreadcrumbList className={"font-bold text-lg sm:text-2xl"}>
                                <BreadcrumbItem>Edit User</BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>{displayName}</BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    )}
                </DialogTitle>
            </DialogHeader>

            <Form onSubmit={userSaveHandler} className={"flex min-h-0 flex-1 flex-col"}>
                <DialogPanel className={"flex flex-col gap-4"}>
                    <DialogDescription>
                        Make changes to the user's profile here. Click save when you&apos;re done.
                    </DialogDescription>
                    {formAlert && (
                        <Alert variant={formAlert.variant}>
                            <AlertTitle>{formAlert.title}</AlertTitle>
                            <AlertDescription>{formAlert.description}</AlertDescription>
                        </Alert>
                    )}
                    <Fieldset>
                        <FieldsetLegend className={"text-xl border-b border-secondary"}>
                            Contact Information
                        </FieldsetLegend>
                        <div className={"flex flex-row gap-4"}>
                            <Field className={"flex-1"}>
                                <FieldLabel>First Name</FieldLabel>
                                <Input type={"text"} name={"givenName"} defaultValue={user.givenName ?? user.name} />
                            </Field>
                            <Field className={"flex-1"}>
                                <FieldLabel>Last Name</FieldLabel>
                                <Input type={"text"} name={"familyName"} defaultValue={user.familyName} />
                            </Field>
                        </div>
                        <div className={"flex flex-row gap-4"}>
                            <Field name={"email"} validate={validateEmailField} className={"flex-1"}>
                                <FieldLabel>
                                    Email
                                    <VerifiedBadge verified={user.verifiedEmail ?? false} />
                                </FieldLabel>
                                <Input type={"email"} name={"email"} defaultValue={user.email} />
                                <FieldError className={"text-destructive"} />
                            </Field>
                            <Field name={"phone"} validate={validatePhoneField} className={"flex-1"}>
                                <FieldLabel>
                                    Phone
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <InfoIcon className={"size-4 text-foreground/70"} />
                                        </TooltipTrigger>
                                        <TooltipPopup className={"flex flex-col"}>
                                            Enter in E.164 phone number format, which
                                            <ul className={"list-disc list-inside"}>
                                                <li>
                                                    is formatted as: [+][country code][subscriber number including area
                                                    code]
                                                </li>
                                                <li>can have a maximum of fifteen digits</li>
                                            </ul>
                                        </TooltipPopup>
                                    </Tooltip>
                                    <VerifiedBadge verified={user.verifiedPhone ?? false} />
                                </FieldLabel>
                                <Input
                                    onChangeCapture={(e) => {
                                        const target = e.target;
                                        if (!(target instanceof HTMLInputElement)) {
                                            return;
                                        }
                                        if (target.name === "phone" && typeof target.value === "string") {
                                            const sanitized = sanitizePhoneInput(target.value);
                                            if (sanitized !== target.value) {
                                                // Mutate the DOM value directly for uncontrolled input before validate reads it.
                                                target.value = sanitized;
                                            }
                                        }
                                    }}
                                    type={"text"}
                                    name={"phone"}
                                    defaultValue={user.phone}
                                />
                                <FieldError className={"text-destructive"} />
                            </Field>
                        </div>
                    </Fieldset>
                    <Fieldset>
                        <FieldsetLegend className={"text-xl border-b border-secondary"}>Credentials</FieldsetLegend>
                        <div className={"flex flex-row gap-4"}>
                            <FormMultiSelectGroup
                                name={"loginIds"}
                                newValueId={"loginIdNewInput"}
                                defaultValues={user.loginIds}
                                newValuePlaceholder={"Enter new Login ID"}
                                fieldLabel={"Login IDs"}
                                preventDuplicates={true}
                            />
                        </div>
                        <div className={"flex flex-col gap-4"}>
                            <PasswordComponent
                                newPasswordName={"newPassword"}
                                confirmPasswordName={"confirmPassword"}
                                enablePasswordName={"passwordEnabled"}
                                expirePasswordName={"passwordExpired"}
                                hasValue={user.password}
                            />
                        </div>
                    </Fieldset>
                    <Fieldset>
                        <FieldsetLegend className={"text-xl border-b border-secondary"}>Access</FieldsetLegend>
                        <div className={"flex flex-col gap-4 sm:flex-row"}>
                            <Field
                                name={"accountRole"}
                                validate={() => (role.length === 0 ? "Account role is required" : null)}
                                className={"w-full"}
                            >
                                <FieldLabel>Account Role</FieldLabel>
                                <Select
                                    name={"accountRole"}
                                    items={ACCOUNT_ROLE_OPTIONS}
                                    value={role}
                                    onValueChange={(value) => setRole(value ?? "")}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={"Select a role..."} />
                                    </SelectTrigger>
                                    <SelectPopup>
                                        {ACCOUNT_ROLE_OPTIONS.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectPopup>
                                </Select>
                                <FieldError className={"text-destructive"} />
                            </Field>
                            <Field name={"organizations"} validate={validateOrganizationsField} className={"grow"}>
                                <FieldLabel>Organizations</FieldLabel>
                                {organizationsLoading ? (
                                    <Skeleton className={"h-8 w-full"} />
                                ) : (
                                    <AccountObjectsCombobox
                                        name={"organizations"}
                                        defaultValues={user.accountObjects}
                                        items={organizations}
                                        filterLabel={"Organizations"}
                                        filterPlaceholder={"Search organizations..."}
                                    />
                                )}
                                <FieldError className={"text-destructive"} />
                            </Field>
                        </div>
                    </Fieldset>
                </DialogPanel>
                <DialogFooter>
                    <Button variant={"outline"} onClick={() => onClose()}>
                        Cancel
                    </Button>
                    <Button type={"submit"}>Save</Button>
                </DialogFooter>
            </Form>
        </>
    );
}
